import React, { memo, useReducer } from 'react';
import { createUseStyle } from '@redlink/assets/styles';
import { Form } from '@redlink/components/organisms';
import {
  Space,
  withPopUp,
  TwoColumnsInputContainer,
} from '@redlink/components/atoms';
import {
  FormSection,
  TextInput,
  PreviewResumeExperienceItem,
} from '@redlink/components/molecules';
import { DataEntryModalTemplate } from '@redlink/components/templates';
import { useLocale } from '@redlink/assets/locale';
import {
  useMutation,
  newResumeExperienceQuery,
  editResumeExperienceQuery,
} from '@redlink/services';
import { initialState, reducer, types } from './store';
import { freshCache } from './cache';

export const ExperienceEditPage = withPopUp(
  memo(({ onDismiss, data, pageName }) => {
    const { styles } = useStyle();
    const { dictionary } = useLocale();

    const [
      { id, company, position, start, end, description, isEditMode },
      dispatch,
    ] = useReducer(reducer, initialState(data));

    const [
      submitExperience,
      { loading: submitExperienceIsLoading },
    ] = useMutation(
      !isEditMode ? newResumeExperienceQuery : editResumeExperienceQuery,
      {
        done: () => onDismiss(),
        update: freshCache({ isEditMode, id, pageName }),
      }
    );

    return (
      <DataEntryModalTemplate>
        <Form
          isBusy={submitExperienceIsLoading}
          actions={[
            {
              title: dictionary.cancel,
              variant: 'secondary',
              onPress: onDismiss,
            },
            {
              title: dictionary.add,
              variant: 'primary',
              onPress: () =>
                submitExperience({
                  id,
                  company,
                  position,
                  start,
                  end,
                  description,
                }),
            },
          ]}
        >
          <FormSection title={dictionary.newExperience}>
            <TextInput
              require
              title={dictionary.company}
              value={company}
              onChangeText={(value) =>
                dispatch({ type: types.SET_COMPANY, payload: value })
              }
            />
            <Space size={16} />
            <TextInput
              require
              title={dictionary.position}
              value={position}
              onChangeText={(value) =>
                dispatch({ type: types.SET_POSITION, payload: value })
              }
            />
            <Space size={16} />
            <TwoColumnsInputContainer>
              <TextInput
                style={styles.dateInput}
                textStyle={styles.dateInputText}
                title={dictionary.start}
                value={start}
                onChangeText={(value) =>
                  dispatch({ type: types.SET_START, payload: value })
                }
              />
              <Space size={16} />
              <TextInput
                style={styles.dateInput}
                textStyle={styles.dateInputText}
                title={dictionary.end}
                value={end}
                onChangeText={(value) =>
                  dispatch({ type: types.SET_END, payload: value })
                }
              />
            </TwoColumnsInputContainer>
            <Space size={16} />
            <TextInput
              multiline
              title={dictionary.description}
              value={description}
              onChangeText={(value) =>
                dispatch({ type: types.SET_DESCRIPTION, payload: value })
              }
            />
          </FormSection>
        </Form>
        <PreviewResumeExperienceItem
          item={{ company, position, start, end, description }}
        />
      </DataEntryModalTemplate>
    );
  })
);

const useStyle = createUseStyle(({ colors, size }) => ({
  dateInput: {
    flex: 1,
  },
  dateInputText: {
    textAlign: 'right',
  },
}));
